import React, { useState, useEffect } from 'react';
import { useJobApplications } from '../../context/JobApplicationContext';
import { HiOutlineXMark, HiOutlineBuildingOffice, HiOutlineBriefcase } from 'react-icons/hi2';
import styles from './JobApplicationForm.module.css';

const PLATFORMS = [
  'LinkedIn', 'Naukri', 'Company Website', 'Referral', 
  'Indeed', 'Glassdoor', 'AngelList', 'Other'
];

const STATUSES = [
  'Applied', 'Assessment', 'Interview', 'Offer', 'Rejected', 'Ghosted'
];

const PRIORITIES = ['Low', 'Medium', 'High'];

const JobApplicationForm = ({ application, onClose, onSuccess }) => {
  const { createApplication, updateApplication, loading } = useJobApplications();
  
  const [formData, setFormData] = useState({
    companyName: '',
    role: '',
    platform: 'LinkedIn',
    jobUrl: '',
    location: '',
    appliedDate: new Date().toISOString().split('T')[0],
    status: 'Applied',
    priority: 'Medium',
    notes: '',
    followUpDate: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (application) {
      setFormData({
        companyName: application.company_name || '',
        role: application.role || '',
        platform: application.platform || 'LinkedIn',
        jobUrl: application.job_url || '',
        location: application.location || '',
        appliedDate: application.applied_date ? application.applied_date.split('T')[0] : '',
        status: application.status || 'Applied',
        priority: application.priority || 'Medium',
        notes: application.notes || '',
        followUpDate: application.follow_up_date ? application.follow_up_date.split('T')[0] : ''
      });
    }
  }, [application]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData.role.trim()) {
      newErrors.role = 'Role is required';
    }

    if (!formData.appliedDate) {
      newErrors.appliedDate = 'Applied date is required';
    }

    if (formData.jobUrl && !isValidUrl(formData.jobUrl)) {
      newErrors.jobUrl = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      if (application) {
        await updateApplication(application.id, formData);
      } else {
        await createApplication(formData);
      }
      onSuccess();
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            {application ? (
              <HiOutlineBuildingOffice className={styles.titleIcon} />
            ) : (
              <HiOutlineBriefcase className={styles.titleIcon} />
            )}
            <div>
              <h2 className={styles.title}>
                {application ? 'Edit Application' : 'Add New Application'}
              </h2>
              <p className={styles.subtitle}>
                {application ? 'Update your job application details' : 'Track a new job opportunity'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <HiOutlineXMark />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.row}>
              <div className="form-group">
                <label htmlFor="companyName" className="form-label">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={`form-control ${errors.companyName ? styles.error : ''}`}
                  placeholder="Enter company name"
                />
                {errors.companyName && (
                  <span className={styles.errorText}>{errors.companyName}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="role" className="form-label">
                  Role *
                </label>
                <input
                  type="text"
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className={`form-control ${errors.role ? styles.error : ''}`}
                  placeholder="Enter job role"
                />
                {errors.role && (
                  <span className={styles.errorText}>{errors.role}</span>
                )}
              </div>
            </div>

            <div className={styles.row}>
              <div className="form-group">
                <label htmlFor="platform" className="form-label">
                  Platform
                </label>
                <select
                  id="platform"
                  name="platform"
                  value={formData.platform}
                  onChange={handleChange}
                  className="form-control"
                >
                  {PLATFORMS.map(platform => (
                    <option key={platform} value={platform}>
                      {platform}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="location" className="form-label">
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Remote / Hybrid / On-site"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="jobUrl" className="form-label">
                Job URL
              </label>
              <input
                type="url"
                id="jobUrl"
                name="jobUrl"
                value={formData.jobUrl}
                onChange={handleChange}
                className={`form-control ${errors.jobUrl ? styles.error : ''}`}
                placeholder="https://..."
              />
              {errors.jobUrl && (
                <span className={styles.errorText}>{errors.jobUrl}</span>
              )}
            </div>

            <div className={styles.row}>
              <div className="form-group">
                <label htmlFor="appliedDate" className="form-label">
                  Applied Date *
                </label>
                <input
                  type="date"
                  id="appliedDate"
                  name="appliedDate"
                  value={formData.appliedDate}
                  onChange={handleChange}
                  className={`form-control ${errors.appliedDate ? styles.error : ''}`}
                />
                {errors.appliedDate && (
                  <span className={styles.errorText}>{errors.appliedDate}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="followUpDate" className="form-label">
                  Follow-up Date
                </label>
                <input
                  type="date"
                  id="followUpDate"
                  name="followUpDate"
                  value={formData.followUpDate}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className="form-group">
                <label htmlFor="status" className="form-label">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-control"
                >
                  {STATUSES.map(status => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="priority" className="form-label">
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="form-control"
                >
                  {PRIORITIES.map(priority => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="notes" className="form-label">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="form-control"
                rows="4"
                placeholder="Add any notes about this application..."
              />
            </div>
          </div>

          <div className={styles.actions}>
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Saving...' : (application ? 'Update Application' : 'Create Application')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobApplicationForm;